import React, { useEffect, useMemo, useRef } from 'react';
import { StepProps } from '../MultiStepModal';
import { useAuth } from '../../../contexts/AuthContext';
import { jobsApi } from '../../../utils/api';
import SignaturePad from '../../../pages/engineer/SignaturePad';

async function tryDecodeBarcodeFromImageFile(file: File): Promise<string | null> {
  try {
    type DetectorLike = { detect: (bmp: ImageBitmap) => Promise<{ rawValue?: string }[]> };
    type Win = Window & { BarcodeDetector?: new (opts: { formats: string[] }) => DetectorLike };
    const BD = (window as Win).BarcodeDetector;
    if (!BD) return null;
    const detector = new BD({ formats: ['code_128', 'code_39', 'ean_13', 'qr_code', 'data_matrix'] });
    const bmp = await createImageBitmap(file);
    const codes = await detector.detect(bmp);
    bmp.close?.();
    const raw = codes?.[0]?.rawValue;
    return raw && String(raw).trim() ? String(raw).trim() : null;
  } catch {
    return null;
  }
}

export default function CertificateTemplateFillSchemaScreenStep({ data, onDataChange }: StepProps) {
  const { user } = useAuth();
  const lastJobLookupQueryRef = useRef<string>('');
  const activeScreenKey = String((data as any)?.activeScreenKey || '').trim();
  const template = (data as any)?.selectedTemplate;
  const schema = template?.schemaJson;

  const filledDataRaw = (data as any)?.filledData;
  const filledData: Record<string, any> = useMemo(() => {
    return filledDataRaw && typeof filledDataRaw === 'object' ? filledDataRaw : {};
  }, [filledDataRaw]);

  const screen = useMemo(() => {
    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    return screens.find((s) => String(s?.key || '').trim() === activeScreenKey) || null;
  }, [schema, activeScreenKey]);

  const fields = useMemo(() => {
    if (!screen) return [];
    const sections: any[] = Array.isArray(screen?.sections) ? screen.sections : [];
    const out: any[] = [];
    for (const sec of sections) {
      const fs: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      for (const f of fs) out.push({ ...f, __sectionTitle: sec?.title || sec?.key || '' });
    }
    return out;
  }, [screen]);

  const autofillCtx = useMemo(() => {
    const customerName = String((data as any)?.customerName || '').trim();
    const customerEmail = String((data as any)?.customerEmail || '').trim();
    const customerPhone = String((data as any)?.customerPhone || '').trim();
    const customerAddress = String((data as any)?.customerAddress || '').trim();
    const customerPostcode = String((data as any)?.postcode || '').trim();

    const siteName = String((data as any)?.siteName || '').trim();
    const siteAddress = String((data as any)?.siteAddress || '').trim();
    const sitePostcode = String((data as any)?.sitePostcode || '').trim();
    const siteType = String((data as any)?.siteType || '').trim();

    const engineerName = String((user as any)?.name || '').trim();
    const engineerEmail = String((user as any)?.email || '').trim();
    const engineerPhone = String((user as any)?.phone || '').trim();
    const engineerGasSafeNumber = String((user as any)?.gasSafeNumber || '').trim();

    return {
      customerName,
      customerEmail,
      customerPhone,
      customerAddress,
      customerPostcode,
      siteName,
      siteAddress,
      sitePostcode,
      siteType,
      engineerName,
      engineerEmail,
      engineerPhone,
      engineerGasSafeNumber,
    };
  }, [data, user]);

  useEffect(() => {
    const normalize = (v: any) =>
      String(v || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');

    const resolve = (field: any): any => {
      const semantic = normalize(field?.semanticTag);
      const pdf = normalize(field?.pdfFieldName);
      const label = normalize(field?.label || field?.displayLabel);
      const hay = `${semantic} ${pdf} ${label}`;

      if (/(customer|client)_(name|full_name)/.test(hay)) return autofillCtx.customerName;
      if (/(customer|client)_email/.test(hay)) return autofillCtx.customerEmail;
      if (/(customer|client)_(phone|tel|telephone|mobile)/.test(hay)) return autofillCtx.customerPhone;
      if (/(customer|client)_(address|addr)/.test(hay)) return autofillCtx.customerAddress;
      if (/(customer|client)_(postcode|post_code|zip)/.test(hay)) return autofillCtx.customerPostcode;

      if (/(site|property)_(name|title)/.test(hay)) return autofillCtx.siteName;
      if (/(site|property)_(address|addr)/.test(hay)) return autofillCtx.siteAddress || autofillCtx.customerAddress;
      if (/(site|property)_(postcode|post_code|zip)/.test(hay)) return autofillCtx.sitePostcode || autofillCtx.customerPostcode;
      if (/(site|property)_(type|category)/.test(hay)) return autofillCtx.siteType;

      if (/(engineer|inspector|technician)_(name|full_name)/.test(hay)) return autofillCtx.engineerName;
      if (/(engineer|inspector|technician)_email/.test(hay)) return autofillCtx.engineerEmail;
      if (/(engineer|inspector|technician)_(phone|tel|telephone|mobile)/.test(hay)) return autofillCtx.engineerPhone;
      if (/(gassafe|gas_safe|gas)_(number|id|registration)/.test(hay)) return autofillCtx.engineerGasSafeNumber;

      return undefined;
    };

    const current = filledData && typeof filledData === 'object' ? (filledData as Record<string, any>) : {};
    const next: Record<string, any> = { ...current };
    let changed = false;

    for (const f of fields) {
      const key = String(f?.pdfFieldName || '').trim();
      if (!key) continue;
      const existing = next[key];
      const hasExisting = existing !== undefined && existing !== null && String(existing).trim() !== '';
      if (hasExisting) continue;

      const v = resolve(f);
      if (v === undefined || v === null || String(v).trim() === '') continue;

      next[key] = v;
      changed = true;
    }

    if (changed) {
      onDataChange({ filledData: next });
    }
  }, [autofillCtx, fields, filledData, onDataChange]);

  useEffect(() => {
    const normalize = (v: any) =>
      String(v || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');

    const looksLikeJobNumberField = (field: any) => {
      const semantic = normalize(field?.semanticTag);
      const pdf = normalize(field?.pdfFieldName);
      const label = normalize(field?.label || field?.displayLabel);
      const hay = `${semantic} ${pdf} ${label}`;
      return /(job|work_order|workorder|wo)_(number|no|id|ref|reference)/.test(hay) || /(job_number|job_no|work_order)/.test(hay);
    };

    const jobField = fields.find((f) => looksLikeJobNumberField(f));
    if (!jobField) return;

    const jobKey = String(jobField?.pdfFieldName || '').trim();
    if (!jobKey) return;

    const q = String((filledData as any)[jobKey] || '').trim();
    if (!q) return;
    if (q === lastJobLookupQueryRef.current) return;

    const handle = setTimeout(async () => {
      try {
        const res = await jobsApi.lookup(q);
        const payload = res.data;
        const dataPayload = payload?.data || payload;
        const job = dataPayload?.job || null;
        const matches = Array.isArray(dataPayload?.matches) ? dataPayload.matches : [];

        if (!job && matches.length !== 1) return;
        const resolved = job || matches[0];
        if (!resolved?.id) return;

        lastJobLookupQueryRef.current = q;

        const patch: any = {
          jobId: String(resolved.id),
        };

        if (resolved.site?.id) {
          patch.siteId = String(resolved.site.id);
          patch.siteName = String(resolved.site.name || '').trim();
          patch.siteAddress = String(resolved.site.address || '').trim();
          patch.sitePostcode = String(resolved.site.postcode || '').trim();
        }

        if (!data.customerId && resolved.client?.id) {
          patch.customerId = String(resolved.client.id);
          patch.isNewCustomer = false;
        }

        if (!String((data as any)?.customerName || '').trim()) patch.customerName = String(resolved.client?.name || '').trim();
        if (!String((data as any)?.customerEmail || '').trim()) patch.customerEmail = String(resolved.client?.email || '').trim();
        if (!String((data as any)?.customerPhone || '').trim()) patch.customerPhone = String(resolved.client?.phone || '').trim();
        if (!String((data as any)?.customerAddress || '').trim()) patch.customerAddress = String(resolved.client?.address || '').trim();
        if (!String((data as any)?.postcode || '').trim()) patch.postcode = String(resolved.client?.postcode || '').trim();

        onDataChange(patch);
      } catch {
        // ignore
      } finally {
      }
    }, 450);

    return () => clearTimeout(handle);
  }, [data, fields, filledData, onDataChange]);

  const setValue = (pdfFieldName: string, value: any) => {
    onDataChange({
      filledData: {
        ...filledData,
        [pdfFieldName]: value,
      },
    });
  };

  if (!activeScreenKey) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Template fields</h3>
          <p className="step-form-subtitle">No screen selected.</p>
        </div>
      </div>
    );
  }

  if (!screen) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Template fields</h3>
          <p className="step-form-subtitle">This template doesn’t have a schema for this screen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">{String(screen?.title || 'Section')}</h3>
        <p className="step-form-subtitle">Fill in this section of the certificate.</p>
      </div>

      <div className="step-form-content">
        {fields.length === 0 ? (
          <div className="text-sm text-[#686E6C]">No fields on this screen.</div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {fields.map((f: any, idx: number) => {
              const pdfFieldName = String(f?.pdfFieldName || '').trim();
              if (!pdfFieldName) return null;

              const label = String(f?.label || f?.displayLabel || pdfFieldName);
              const fieldType = String(f?.fieldType || 'text');
              const required = Boolean(f?.required);

              const v = filledData[pdfFieldName];

              if (fieldType === 'checkbox') {
                return (
                  <label key={pdfFieldName} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(v)}
                      onChange={(e) => setValue(pdfFieldName, e.target.checked)}
                    />
                    <span className="text-[#181818]">
                      {label}
                      {required ? ' *' : ''}
                    </span>
                  </label>
                );
              }

              if (fieldType === 'signature') {
                return (
                  <div key={pdfFieldName} className="form-group">
                    <div className="form-label">
                      {label}
                      {required ? ' *' : ''}
                    </div>
                    <SignaturePad
                      height={160}
                      value={typeof v === 'string' ? v : null}
                      onChange={(dataUrl) => setValue(pdfFieldName, dataUrl)}
                    />
                  </div>
                );
              }

              const options: { value: string; label: string }[] = Array.isArray(f?.options)
                ? f.options
                    .map((o: any) => ({
                      value: String(o?.value ?? '').trim(),
                      label: String(o?.label ?? o?.value ?? '').trim(),
                    }))
                    .filter((o: { value: string }) => o.value.length > 0)
                : [];

              if (fieldType === 'radio' && options.length > 0) {
                return (
                  <div key={pdfFieldName} className="form-group">
                    <div className="form-label">
                      {label}
                      {required ? ' *' : ''}
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {options.map((opt) => (
                        <label key={`${pdfFieldName}-${opt.value}`} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name={pdfFieldName}
                            value={opt.value}
                            checked={String(v ?? '') === opt.value}
                            onChange={() => setValue(pdfFieldName, opt.value)}
                          />
                          <span className="text-[#181818]">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              }

              if (fieldType === 'select' && options.length > 0) {
                const sv = typeof v === 'string' || typeof v === 'number' ? String(v) : '';
                return (
                  <div key={pdfFieldName} className="form-group">
                    <label className="form-label" htmlFor={`fld-${pdfFieldName}`}>
                      {label}
                      {required ? ' *' : ''}
                    </label>
                    <select
                      id={`fld-${pdfFieldName}`}
                      className="form-input"
                      value={sv}
                      onChange={(e) => setValue(pdfFieldName, e.target.value)}
                    >
                      <option value="">Select…</option>
                      {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (fieldType === 'date') {
                const dv = typeof v === 'string' ? v : '';
                return (
                  <div key={pdfFieldName} className="form-group">
                    <label className="form-label" htmlFor={`fld-${pdfFieldName}`}>
                      {label}
                      {required ? ' *' : ''}
                    </label>
                    <input
                      id={`fld-${pdfFieldName}`}
                      type="date"
                      className="form-input"
                      value={dv}
                      onChange={(e) => setValue(pdfFieldName, e.target.value)}
                    />
                  </div>
                );
              }

              if (fieldType === 'number') {
                return (
                  <div key={pdfFieldName} className="form-group">
                    <label className="form-label" htmlFor={`fld-${pdfFieldName}`}>
                      {label}
                      {required ? ' *' : ''}
                    </label>
                    <input
                      id={`fld-${pdfFieldName}`}
                      type="number"
                      inputMode="decimal"
                      className="form-input"
                      value={typeof v === 'number' ? String(v) : typeof v === 'string' ? v : ''}
                      onChange={(e) => setValue(pdfFieldName, e.target.value)}
                    />
                    {f?.hint ? <p className="mt-1 text-xs text-[#686E6C]">{String(f.hint)}</p> : null}
                  </div>
                );
              }

              const serialCapture = Boolean(f?.serialCapture);

              return (
                <div key={pdfFieldName} className="form-group">
                  <label className="form-label" htmlFor={`fld-${pdfFieldName}`}>
                    {label}
                    {required ? ' *' : ''}
                  </label>
                  <input
                    id={`fld-${pdfFieldName}`}
                    type="text"
                    className="form-input"
                    value={typeof v === 'string' || typeof v === 'number' ? String(v) : ''}
                    onChange={(e) => setValue(pdfFieldName, e.target.value)}
                    autoComplete="off"
                  />
                  {serialCapture ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <label className="inline-flex cursor-pointer items-center rounded-md border border-[#CDCDCD] bg-white px-3 py-1.5 text-xs font-medium text-[#383838] hover:bg-[#F3F2F0]">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="sr-only"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            const decoded = await tryDecodeBarcodeFromImageFile(file);
                            if (decoded) setValue(pdfFieldName, decoded);
                          }}
                        />
                        Scan barcode / plate
                      </label>
                      <span className="text-[11px] text-[#686E6C]">Camera fills this field when a code is detected (supported browsers).</span>
                    </div>
                  ) : null}
                  {f?.hint && !serialCapture ? <p className="mt-1 text-xs text-[#686E6C]">{String(f.hint)}</p> : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
